import { LocalizedDatePipe } from '../../../shared/pipe/localized-date.pipe';
import { CommonModule } from '@angular/common';
import {
  Component,
  DestroyRef,
  OnInit,
  computed,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { Observable, forkJoin, of } from 'rxjs';

import { ConfirmModalComponent } from '../../../shared/components/ui/confirm-modal/confirm-modal.component';
import { DataStateComponent } from '../../../shared/components/ui/data-state/data-state.component';
import { ModalComponent } from '../../../shared/components/ui/modal/modal.component';
import { PageHeaderComponent } from '../../../shared/components/ui/page-header/page-header.component';
import { toErrorMessage } from '../../../shared/helpers/http-error';
import {
  byBusinessThenAlpha,
  filterBusinessRoles,
  isBusinessRole,
  stripRolePrefix,
} from '../../../shared/helpers/role.helpers';
import {
  fullName as userFullName,
  initials as userInitials,
} from '../../../shared/helpers/user.helpers';
import { RolesService } from '../../../shared/services/roles.service';
import { UserRolesService } from '../../../shared/services/user-roles.service';
import { UsersService } from '../../../shared/services/users.service';
import { RoleDTO } from '../../../shared/types/role.types';
import { User } from '../../../shared/types/user.types';

@Component({
  selector: 'app-user-detail',
  imports: [
    CommonModule,
    RouterModule,
    ReactiveFormsModule,
    LocalizedDatePipe,
    ModalComponent,
    ConfirmModalComponent,
    DataStateComponent,
    PageHeaderComponent,
  ],
  templateUrl: './user-detail.component.html',
})
export class UserDetailComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly usersService = inject(UsersService);
  private readonly rolesService = inject(RolesService);
  private readonly userRolesService = inject(UserRolesService);
  private readonly destroyRef = inject(DestroyRef);

  // ── state ──────────────────────────────────────────────────────────────
  readonly user = signal<User | null>(null);
  readonly loading = signal(true);
  readonly loadError = signal<string | null>(null);

  // delete modal
  readonly deleteOpen = signal(false);
  readonly deleting = signal(false);
  readonly deleteError = signal<string | null>(null);

  // regen-password modal
  readonly regenOpen = signal(false);
  readonly regenLoading = signal(false);
  readonly regenError = signal<string | null>(null);
  readonly regenResult = signal<string | null>(null);

  // roles modal
  readonly rolesOpen = signal(false);
  readonly rolesLoading = signal(false);
  readonly rolesSubmitting = signal(false);
  readonly rolesError = signal<string | null>(null);
  readonly availableRoles = signal<RoleDTO[]>([]);
  readonly selectedRoles = signal<Set<string>>(new Set());
  readonly rolesFilterCtrl = new FormControl<string>('', { nonNullable: true });
  private readonly rolesFilter = signal('');

  readonly visibleRoles = computed(() => {
    const q = this.rolesFilter().toLowerCase().trim();
    const list = q
      ? this.availableRoles().filter(
          (r) =>
            r.name.toLowerCase().includes(q) ||
            (r.description?.toLowerCase().includes(q) ?? false),
        )
      : this.availableRoles();
    return list;
  });

  readonly rolesDirty = computed(() => {
    const u = this.user();
    if (!u) return false;
    const initial = new Set((u.roles ?? []).map(stripRolePrefix));
    const target = this.selectedRoles();
    if (initial.size !== target.size) return true;
    for (const r of target) if (!initial.has(r)) return true;
    return false;
  });

  private id!: number;

  // ── derived helpers ────────────────────────────────────────────────────
  readonly initials = computed(() => userInitials(this.user()));
  readonly fullName = computed(() => userFullName(this.user()));
  readonly businessRoles = computed(() => filterBusinessRoles(this.user()?.roles));

  // expose helper for templates
  readonly isBusinessRole = isBusinessRole;

  // ── lifecycle ──────────────────────────────────────────────────────────
  ngOnInit(): void {
    const idParam = this.route.snapshot.paramMap.get('id');
    const parsed = idParam ? Number(idParam) : NaN;
    if (!Number.isFinite(parsed)) {
      this.loadError.set('Identifiant invalide.');
      this.loading.set(false);
      return;
    }
    this.id = parsed;
    this.fetch();

    this.rolesFilterCtrl.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((v) => this.rolesFilter.set(v ?? ''));
  }

  // ── actions ────────────────────────────────────────────────────────────
  fetch(): void {
    this.loading.set(true);
    this.loadError.set(null);
    this.usersService
      .get(this.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (u) => {
          this.user.set(u);
          this.loading.set(false);
        },
        error: (err: unknown) => {
          this.loading.set(false);
          this.loadError.set(
            toErrorMessage(err, 'Impossible de charger cet utilisateur.'),
          );
        },
      });
  }

  // delete
  openDelete(): void { this.deleteError.set(null); this.deleteOpen.set(true); }
  closeDelete(): void { this.deleteOpen.set(false); }
  confirmDelete(): void {
    this.deleting.set(true);
    this.deleteError.set(null);
    this.usersService.delete(this.id).subscribe({
      next: () => {
        this.deleting.set(false);
        this.deleteOpen.set(false);
        void this.router.navigate(['/users']);
      },
      error: (err: unknown) => {
        this.deleting.set(false);
        this.deleteError.set(toErrorMessage(err, 'errors.deleteFailed'));
      },
    });
  }

  // regen
  openRegen(): void {
    this.regenError.set(null);
    this.regenResult.set(null);
    this.regenOpen.set(true);
  }
  closeRegen(): void {
    if (this.regenLoading()) return;
    this.regenOpen.set(false);
    this.regenResult.set(null);
  }
  confirmRegen(): void {
    this.regenLoading.set(true);
    this.regenError.set(null);
    this.usersService.regeneratePassword(this.id).subscribe({
      next: (updated) => {
        this.regenLoading.set(false);
        const pwd = updated.plainPassword;
        this.regenResult.set(
          pwd ?? "Mot de passe régénéré. L'utilisateur le recevra par email.",
        );
        this.user.set(updated);
      },
      error: (err: unknown) => {
        this.regenLoading.set(false);
        this.regenError.set(toErrorMessage(err, 'La régénération a échoué.'));
      },
    });
  }
  copyResult(): void {
    const value = this.regenResult();
    if (!value) return;
    void navigator.clipboard?.writeText(value).catch(() => {});
  }

  // roles modal
  openRoles(): void {
    this.rolesError.set(null);
    this.rolesOpen.set(true);
    this.rolesFilterCtrl.setValue('', { emitEvent: false });
    this.rolesFilter.set('');
    this.selectedRoles.set(
      new Set((this.user()?.roles ?? []).map(stripRolePrefix)),
    );
    if (this.availableRoles().length === 0) {
      this.loadAvailableRoles();
    }
  }
  closeRoles(): void {
    if (this.rolesSubmitting()) return;
    this.rolesOpen.set(false);
  }
  private loadAvailableRoles(): void {
    this.rolesLoading.set(true);
    this.rolesService.list().subscribe({
      next: (roles) => {
        // Sort once here so `visibleRoles` doesn't re-sort on every filter keystroke.
        this.availableRoles.set([...(roles ?? [])].sort(byBusinessThenAlpha));
        this.rolesLoading.set(false);
      },
      error: (err: unknown) => {
        this.rolesLoading.set(false);
        this.rolesError.set(
          toErrorMessage(err, 'Impossible de charger la liste des rôles.'),
        );
      },
    });
  }
  toggleRole(name: string): void {
    const next = new Set(this.selectedRoles());
    if (next.has(name)) next.delete(name);
    else next.add(name);
    this.selectedRoles.set(next);
  }
  isRoleSelected(name: string): boolean {
    return this.selectedRoles().has(name);
  }
  saveRoles(): void {
    const u = this.user();
    if (!u || !this.rolesDirty()) {
      this.rolesOpen.set(false);
      return;
    }
    const initial = new Set((u.roles ?? []).map(stripRolePrefix));
    const target = this.selectedRoles();
    const added = [...target].filter((r) => !initial.has(r));
    const removed = [...initial].filter((r) => !target.has(r));

    this.rolesSubmitting.set(true);
    this.rolesError.set(null);

    const ops: Observable<void>[] = [];
    if (added.length) {
      ops.push(
        this.userRolesService.assign({
          userIdentifier: u.username,
          roleNames: added,
        }),
      );
    }
    if (removed.length) {
      ops.push(
        this.userRolesService.remove({
          userIdentifier: u.username,
          roleNames: removed,
        }),
      );
    }

    // Guard against the `forkJoin([])` no-emit gotcha — though `rolesDirty` already filters.
    const wave$ = ops.length ? forkJoin(ops) : of([]);

    wave$.subscribe({
      next: () => {
        this.rolesSubmitting.set(false);
        this.rolesOpen.set(false);
        this.fetch();
      },
      error: (err: unknown) => {
        this.rolesSubmitting.set(false);
        this.rolesError.set(
          toErrorMessage(err, 'La mise à jour des rôles a échoué.'),
        );
      },
    });
  }
}
